import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Search, X, Wrench, CheckCircle2, PlayCircle, RotateCcw } from 'lucide-react';
import { theme } from '../shared/theme';
import { portalFetch } from '../shared/api';
import { pollWhileActive, messageTime } from '../shared/messaging';
import { useLanguage } from '../../components/LanguageContext';
import '../shared/portals.css';

// ============================================================
// AdminIssues — every service request across the portfolio, in one queue.
//
// The question this screen answers is "what needs doing today", and until
// now the only way to answer it was to open each property in turn and read
// its maintenance tab. So the list is portfolio-wide and each row says which
// unit it belongs to — twice over, because that is the thing being looked
// for: in words next to the reference, and as a colour down the left edge so
// the eye can group a building's issues without reading a single name.
//
// Open first. Resolved is an archive, and an archive is not a work queue.
// ============================================================

// Two minutes, not thirty seconds. Each call reads three whole collections
// server-side, and a repair request does not arrive twice a minute. The idle
// guard in pollWhileActive stops it entirely once nobody is touching the
// screen, and returning to the app refreshes immediately — so a slower tick
// costs nothing anyone can see.
const POLL_MS = 120000;

export interface PmIssue {
  id: string;
  unitId: string | null;
  unitName: string;
  unitProject: string;
  unitNumber: string;
  tenantName: string;
  category: string;
  title: string;
  detail: string;
  status: string;
  cost: number;
  source: 'ea' | 'tenant';
  createdAt: string | null;
}

// A stable colour per unit, so the same property is the same colour every
// time the list is opened and on every device. Hashed from the id rather
// than assigned by position, which would reshuffle whenever the list does.
const STRIPES = ['#D6B160', '#2563eb', '#16a34a', '#d946ef', '#ea580c', '#0891b2', '#7c3aed', '#dc2626'];
function stripeFor(key: string): string {
  let h = 0;
  for (let i = 0; i < key.length; i += 1) h = (h * 31 + key.charCodeAt(i)) >>> 0;
  return STRIPES[h % STRIPES.length];
}

// The reference the unit is known by. `name` and `project` disagree on some
// records, so the row shows the specific one it has and falls back rather
// than picking blind — never `name || project`, which quietly swaps between
// two different labels for the same property.
function unitLabel(i: PmIssue, unknown: string): string {
  if (i.unitName) return i.unitName;
  if (i.unitProject && i.unitNumber) return `${i.unitProject} ${i.unitNumber}`;
  if (i.unitProject) return i.unitProject;
  if (i.unitNumber) return i.unitNumber;
  return unknown;
}

// Explicit, not a dynamic `t('isStatus_' + s)` key: a status we did not
// anticipate would then render as the raw key on screen, and the i18n parity
// check cannot see keys that are built at runtime.
function statusLabel(status: string, t: (k: string) => string): string {
  const s = (status || '').toLowerCase();
  if (s === 'resolved') return t('isStatusResolved');
  if (s === 'in_progress' || s === 'in_review') return t('isStatusInProgress');
  if (s === 'submitted') return t('isStatusNew');
  return t('isStatusOpen');
}

function statusTone(status: string): { bg: string; fg: string } {
  const s = (status || '').toLowerCase();
  if (s === 'resolved') return { bg: theme.successBg, fg: theme.success };
  if (s === 'in_progress' || s === 'in_review') return { bg: theme.infoBg, fg: theme.info };
  if (s === 'submitted') return { bg: '#fdf6e6', fg: '#8a6d1f' };
  return { bg: theme.warningBg, fg: theme.warning };
}

// No props: see the note in AdminProperties. One door out, in the header.
type Props = Record<string, never>;

const AdminIssues: React.FC<Props> = () => {
  const { t, language } = useLanguage();
  const locale = language === 'de' ? 'de-DE' : 'en-GB';

  const [status, setStatus] = useState<'open' | 'resolved'>('open');
  const [q, setQ] = useState('');
  const [issues, setIssues] = useState<PmIssue[]>([]);
  const [counts, setCounts] = useState<{ open: number; resolved: number; total: number }>({ open: 0, resolved: 0, total: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const inFlight = useRef(false);

  const load = useCallback(async (opts: { silent?: boolean } = {}) => {
    if (inFlight.current) return;
    inFlight.current = true;
    if (!opts.silent) setLoading(true);
    try {
      const res = await portalFetch<{ issues: PmIssue[]; counts: typeof counts }>(
        `/pm/issues?status=${status}${q ? `&q=${encodeURIComponent(q)}` : ''}`,
      );
      setIssues(res.issues || []);
      setCounts(res.counts || { open: 0, resolved: 0, total: 0 });
      setError(null);
    } catch (e: any) {
      if (!opts.silent) setError(e?.message || t('isLoadFailed'));
    } finally {
      inFlight.current = false;
      setLoading(false);
    }
  }, [status, q, t]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => pollWhileActive(() => load({ silent: true }), POLL_MS), [load]);

  // A work queue you cannot act on is half a tool. The status moves here
  // rather than in another window.
  //
  // Optimistic, then reconciled: the row changes the instant it is tapped,
  // because waiting on a cold container to redraw a chip feels broken — and
  // it goes back if the server refuses. The list is NOT reloaded on success;
  // under the "open" filter that would make the row vanish mid-tap, which
  // reads as losing the thing you just touched. The next poll moves it.
  const setStatus2 = useCallback(async (id: string, next: string) => {
    const before = issues;
    setIssues((prev) => prev.map((i) => (i.id === id ? { ...i, status: next } : i)));
    setCounts((c) => {
      const was = before.find((i) => i.id === id);
      if (!was) return c;
      const wasResolved = was.status.toLowerCase() === 'resolved';
      const nowResolved = next === 'resolved';
      if (wasResolved === nowResolved) return c;
      return nowResolved
        ? { ...c, open: Math.max(0, c.open - 1), resolved: c.resolved + 1 }
        : { ...c, open: c.open + 1, resolved: Math.max(0, c.resolved - 1) };
    });
    try {
      await portalFetch(`/pm/maintenance/${encodeURIComponent(id)}/status`, {
        method: 'POST',
        body: JSON.stringify({ status: next }),
      });
      setError(null);
    } catch (e: any) {
      setIssues(before);
      setError(e?.message || t('isUpdateFailed'));
    }
  }, [issues, t]);

  return (
    <div className="pt-fade" style={{ padding: 14 }}>
      {/* Everything the console does that this list does not is one tap away,
          rather than this screen pretending to be the whole console. */}

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: `1px solid ${theme.line}`, borderRadius: 12, padding: '9px 12px', marginBottom: 10 }}>
        <Search size={17} color={theme.slateLight} />
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('isSearch')}
          style={{ border: 'none', outline: 'none', fontSize: 14.5, flex: 1, background: 'transparent', padding: 0, width: 'auto' }}
        />
        {q && (
          <button onClick={() => setQ('')} aria-label={t('inbClear')} style={{ background: 'none', border: 'none', padding: 0, minHeight: 0, color: theme.slateLight }}>
            <X size={16} />
          </button>
        )}
      </div>

      <div style={{ display: 'flex', gap: 7, marginBottom: 12 }}>
        {([['open', t('isOpenCases'), counts.open, Wrench], ['resolved', t('isResolved'), counts.resolved, CheckCircle2]] as const).map(
          ([key, label, n, Icon]) => {
            const on = status === key;
            return (
              <button
                key={key}
                onClick={() => setStatus(key as 'open' | 'resolved')}
                className="pt-tap"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 6,
                  background: on ? theme.navy : '#fff',
                  color: on ? '#fff' : theme.slate,
                  border: `1px solid ${on ? theme.navy : theme.line}`,
                  borderRadius: 99, padding: '7px 13px', minHeight: 0,
                  fontSize: 12.5, fontWeight: 700, cursor: 'pointer',
                }}
              >
                <Icon size={14} /> {label}
                <span style={{ opacity: 0.75 }}>{n}</span>
              </button>
            );
          },
        )}
      </div>

      {error && <div className="pm-chat-error" style={{ margin: '0 0 10px' }}>{error}</div>}

      {loading && !issues.length && (
        <>
          <div className="pt-skel" style={{ height: 76, marginBottom: 8 }} />
          <div className="pt-skel" style={{ height: 76, marginBottom: 8 }} />
          <div className="pt-skel" style={{ height: 76 }} />
        </>
      )}

      {!loading && !issues.length && (
        <div className="pm-chat-empty">
          <Wrench size={30} color={theme.gold} />
          <div className="pm-chat-empty-title">{status === 'open' ? t('isNoneOpen') : t('isNoneResolved')}</div>
          <div className="pm-chat-empty-text">{t('isEmptyText')}</div>
        </div>
      )}

      {issues.map((i) => {
        const tone = statusTone(i.status);
        const unit = unitLabel(i, t('isUnknownUnit'));
        const st = (i.status || '').toLowerCase();
        const resolved = st === 'resolved';
        const inProgress = st === 'in_progress' || st === 'in_review';
        return (
          <div key={i.id} className="pm-issue-row" style={{ borderLeftColor: stripeFor(i.unitId || unit) }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 7, flexWrap: 'wrap' }}>
                <span className="pm-issue-ref">#{i.id.slice(-4).toUpperCase()}</span>
                {/* The unit, in words, right beside the reference — this is
                    what the row is looked up by. */}
                <span className="pm-issue-unit">{unit}</span>
              </div>
              <div className="pm-issue-title">{i.title || i.category}</div>
              <div className="pm-issue-meta">
                {i.category}
                {i.tenantName ? ` · ${i.tenantName}` : ''}
                {i.source === 'tenant' ? ` · ${t('isFromTenant')}` : ''}
                {i.createdAt ? ` · ${messageTime(i.createdAt, locale)}` : ''}
              </div>
            </div>
            <div className="pm-issue-side">
              <span className="pm-issue-status" style={{ background: tone.bg, color: tone.fg }}>
                {statusLabel(i.status, t)}
              </span>
              {/* One action per state, never a menu of them: the next thing
                  to do with a repair is rarely ambiguous. */}
              {resolved ? (
                <button onClick={() => setStatus2(i.id, 'open')} className="pt-tap pm-issue-act">
                  <RotateCcw size={12} /> {t('isReopen')}
                </button>
              ) : inProgress ? (
                <button onClick={() => setStatus2(i.id, 'resolved')} className="pt-tap pm-issue-act is-done">
                  <CheckCircle2 size={12} /> {t('isResolve')}
                </button>
              ) : (
                <button onClick={() => setStatus2(i.id, 'in_progress')} className="pt-tap pm-issue-act">
                  <PlayCircle size={12} /> {t('isStart')}
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default AdminIssues;
